import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Pagination from "@mui/material/Pagination";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import { branchSwitchContext } from "../contexts/BranchSwitchProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import { dataContext } from "../contexts/DataProvider";
import { errorContext } from "../contexts/ErrorProvider";
import ProcessData from "../utils/ProcessData";
import validate from "../validation/validator";
import { webhookSchema } from "../validation/schema";
import {
  fetchWebhooks,
  createNewWebhook,
  editWebhook,
  deleteWebhook,
} from "../services/webhookService";
import { fetchAPIKey, regenerateAPIKey } from "../services/apiKeyService";

const AllWebhooks = () => {
  const [data, setData] = useContext(dataContext);
  const [errors, setErrors] = useContext(errorContext);
  const { branchSwitch } = useContext(branchSwitchContext);
  const [webhooks, setWebhooks] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [modal, setModal] = useState({ open: false, action: "", data: "" });
  const [copied, setCopied] = useState(false);
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const getWebhooks = useCallback(async () => {
    setIsLoading(true);
    const response = await fetchWebhooks(page, limit, search);
    if (response.data.code === 1) {
      setWebhooks(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading]);

  const getApiKey = useCallback(async () => {
    const response = await fetchAPIKey();
    if (response.data.code === 1) {
      setApiKey(response.data.data);
    }
  }, []);

  const copyApiKey = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  }, [apiKey]);

  const regenerate = useCallback(async () => {
    const response = await regenerateAPIKey();
    if (response.data.code === 1) {
      setModal({ open: false, action: "", data: "" });
      getApiKey();
    }
    // eslint-disable-next-line
  }, [data, errors]);

  const add = useCallback(async () => {
    const validation = validate(data, setErrors, webhookSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await createNewWebhook(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getWebhooks();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);

  const update = useCallback(async () => {
    const validation = validate(data, setErrors, webhookSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await editWebhook(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getWebhooks();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);

  const trash = useCallback(async () => {
    const validation = validate(data, setErrors, webhookSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await deleteWebhook(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getWebhooks();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getWebhooks();
  }, [page, search, getWebhooks, branchSwitch]);

  useEffect(() => {
    getApiKey();
  }, [branchSwitch, getApiKey]);

  const renderWebhookList = (webhooks) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {webhooks?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Webhook URL</th>
                    <th scope="col">Created At</th>
                    <th scope="col">Last Updated</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks?.map((webhook, index) => (
                    <tr key={index}>
                      <td>{webhook?.webhook}</td>
                      <td>
                        {moment(webhook?.createdAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY hh:mm A")}
                      </td>
                      <td>
                        {moment(webhook?.updatedAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY hh:mm A")}
                      </td>
                      <td>
                        <div className="btn-list">
                          <button
                            onClick={() => {
                              setData(webhook);
                              setModal({
                                open: true,
                                action: "edit",
                                data: "",
                              });
                            }}
                            title="Edit"
                            className="btn btn-sm btn-icon btn-primary-light btn-wave rounded-circle waves-effect waves-light"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => {
                              setData(webhook);
                              setModal({
                                open: true,
                                action: "trash",
                                data: "",
                              });
                            }}
                            title="Delete"
                            className="btn btn-sm btn-icon btn-danger-light btn-wave rounded-circle waves-effect waves-light"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="6">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Manage Webhooks
                        </span>
                      </div>
                    </div>
                    <div className="d-block d-md-flex flex-wrap gap-2">
                      <div className="mb-3">
                        <input
                          className="form-control form-control-md"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div className="mb-3">
                        <button
                          onClick={() => {
                            setModal({
                              open: true,
                              action: "add",
                              data: "",
                            });
                          }}
                          className="btn btn-primary btn-md w-100 w-md-auto"
                        >
                          <i className="ri-add-line me-1 fw-medium align-middle"></i>
                          New Webhook
                        </button>
                      </div>
                      <div className="mb-3">
                        <div className="w-100 w-md-auto d-flex gap-1">
                          <div className="flex-fill">
                            <button
                              onClick={() => {
                                setModal({
                                  open: true,
                                  action: "view_api_key",
                                  data: "",
                                });
                              }}
                              className="btn btn-primary btn-md w-100"
                              title="View Branch API Key"
                            >
                              <i className="ri-eye-line fw-medium align-middle"></i>
                            </button>
                          </div>
                          <div className="flex-fill">
                            <button
                              onClick={() => {
                                setModal({
                                  open: true,
                                  action: "regenerate_api_key",
                                  data: "",
                                });
                              }}
                              className="btn btn-primary btn-md w-100"
                              title="Regenerate Branch API Key"
                            >
                              <i className="ri-refresh-line fw-medium align-middle"></i>
                            </button>
                          </div>
                          <div className="flex-fill">
                            <button
                              onClick={copyApiKey}
                              className="btn btn-primary btn-md w-100"
                              title="Copy Branch API Key"
                            >
                              <i
                                className={`ri-clipboard-${
                                  copied ? "fill" : "line"
                                } fw-medium align-middle`}
                              ></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {renderWebhookList(webhooks)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Begin:Add Webhook */}
          <div
            className={
              modal?.open === true && modal?.action === "add"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "add"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-md"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Add Webhook</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Webhook URL"
                        autoComplete="off"
                        value={data?.webhook ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "webhook",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.webhook ?? ""}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={add}
                      className="btn btn-primary"
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Add Webhook */}

          {/* Begin:Edit Webhook */}
          <div
            className={
              modal?.open === true && modal?.action === "edit"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "edit"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-md"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Edit Webhook</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Webhook URL"
                        autoComplete="off"
                        value={data?.webhook ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "webhook",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.webhook ?? ""}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={update}
                      className="btn btn-primary"
                      type="button"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Edit Webhook */}

          {/* Begin:Delete */}
          <div
            className={
              modal?.open === true && modal?.action === "trash"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "trash"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Delete Employee</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={trash}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Delete */}

          {/* Begin:View API Key */}
          <div
            className={
              modal?.open === true && modal?.action === "view_api_key"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "view_api_key"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-md"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Branch API Key</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12 text-center mb-3">
                      <p className="text-break">
                        {apiKey?.trim()
                          ? apiKey
                          : "No API key generated please regenerate"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:View API Key */}

          {/* Begin:Regenerate API Key */}
          <div
            className={
              modal?.open === true && modal?.action === "regenerate_api_key"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "regenerate_api_key"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Regenerate API Key</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={regenerate}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Regenerate API Key */}
        </div>
        <Footer />
      </div>
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default AllWebhooks;
